import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { ETATS_SALLE, type EtatSalle } from '@artosera/shared';
import { useSalles } from '../../api/viewingRooms.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { galerieALaction } from '../../auth/sujet.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { useShellContext } from '../shell/context.js';
import { NouvelleSalleDialog } from './NouvelleSalleDialog.js';
import { dateSalle, EtatDeSalle, texteSalle, VignetteSalle } from './salleUi.js';
import styles from './ViewingRoomsPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function ViewingRoomsPage() {
  const { t, i18n } = useTranslation();
  const { galerie, galerieSlug } = useShellContext();
  const [params, setParams] = useSearchParams();
  const [etat, setEtat] = useState<EtatSalle | undefined>();
  const [page, setPage] = useState(1);
  const etats = [undefined, ...ETATS_SALLE];
  const salles = useSalles(galerieSlug, { etat, page });
  const peutEcrire = galerieALaction(galerie, 'viewing-rooms:write');
  const ouvert = peutEcrire && params.get('nouvelle') === '1';
  const initiale = useOeuvre(galerieSlug, ouvert ? params.get('oeuvre') : null);
  const fermer = () => {
    const suite = new URLSearchParams(params);
    suite.delete('nouvelle');
    suite.delete('oeuvre');
    setParams(suite, { replace: true });
  };
  return (
    <div className={styles.page} data-ui="viewing-rooms-page">
      <header className={styles.entete}>
        <div>
          <h1>{t('regie:viewingRooms.titre')}</h1>
          <p>{t('regie:viewingRooms.sousTitre')}</p>
        </div>
        <Verrouillable action="viewing-rooms:write" ouvert={peutEcrire}>
          <Button onClick={() => setParams({ nouvelle: '1' })}>
            {t('regie:viewingRooms.nouvelle')}
          </Button>
        </Verrouillable>
      </header>
      <div className={styles.filtres} role="radiogroup" aria-label={t('regie:viewingRooms.titre')}>
        {etats.map((valeur, index) => (
          <button
            key={valeur ?? 'toutes'}
            role="radio"
            aria-checked={etat === valeur}
            className={styles.filtre}
            tabIndex={etat === valeur ? 0 : -1}
            onKeyDown={(event) => {
              if (
                !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(
                  event.key,
                )
              )
                return;
              event.preventDefault();
              const suivant =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? etats.length - 1
                    : (index +
                        (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) +
                        etats.length) %
                      etats.length;
              setEtat(etats[suivant]);
              setPage(1);
              event.currentTarget.parentElement
                ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
                [suivant]?.focus();
            }}
            onClick={() => {
              setEtat(valeur);
              setPage(1);
            }}
          >
            {t(valeur ? `regie:viewingRooms.etats.${valeur}` : 'regie:viewingRooms.filtres.toutes')}
          </button>
        ))}
      </div>
      {salles.isLoading ? (
        <div role="status" className={styles.squelette}>
          {t('regie:viewingRooms.chargement')}
        </div>
      ) : salles.isError ? (
        <p role="alert">{t('regie:viewingRooms.erreurs.generique')}</p>
      ) : salles.data?.salles.length === 0 ? (
        <p className={styles.vide}>{t('regie:viewingRooms.vide')}</p>
      ) : (
        <div className={styles.grille}>
          {salles.data?.salles.map((salle) => (
            <article className={styles.carte} key={salle.id} data-ui="viewing-room-card">
              <div className={styles.bande}>
                {salle.oeuvres.slice(0, 4).map((oeuvre) => (
                  <VignetteSalle
                    key={oeuvre.id}
                    galerieSlug={galerieSlug}
                    mediaId={oeuvre.mediaPrincipalId}
                  />
                ))}
                {salle.oeuvres.length > 4 && <span>+ {salle.oeuvres.length - 4}</span>}
              </div>
              <EtatDeSalle etat={salle.etat} />
              <h2 className={styles.titreSalle}>
                <Link to={salle.id}>{texteSalle(salle.titre, i18n.language)}</Link>
              </h2>
              <div className={styles.contact}>
                <Monogramme nom={salle.contact?.nomAffiche} taille={32} />
                <span>{salle.contact?.nomAffiche ?? t('regie:viewingRooms.sansContact')}</span>
              </div>
              <p className={styles.meta}>
                {t('regie:viewingRooms.expire', { date: dateSalle(salle.expireLe, i18n.language) })}
              </p>
              <p className={styles.compteurs}>
                {t('regie:viewingRooms.ouvertures.total', { count: salle.ouvertures.total })} ·{' '}
                {t('regie:viewingRooms.ouvertures.visiteurs', {
                  count: salle.ouvertures.visiteurs,
                })}
              </p>
            </article>
          ))}
        </div>
      )}
      {(salles.data?.pagination.pages ?? 0) > 1 && (
        <nav
          className={styles.pagination}
          aria-label={t('regie:viewingRooms.page', { page, pages: salles.data?.pagination.pages })}
        >
          <Button variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t('regie:viewingRooms.precedente')}
          </Button>
          <span>
            {page} / {salles.data?.pagination.pages}
          </span>
          <Button
            variant="secondary"
            disabled={page >= (salles.data?.pagination.pages ?? 1)}
            onClick={() => setPage(page + 1)}
          >
            {t('regie:viewingRooms.suivante')}
          </Button>
        </nav>
      )}
      {ouvert && (!params.get('oeuvre') || initiale.data) && (
        <NouvelleSalleDialog
          galerieSlug={galerieSlug}
          ouvert
          onFermer={fermer}
          oeuvreInitiale={initiale.data?.oeuvre}
        />
      )}
      {ouvert && initiale.isError && (
        <p role="alert">{t('regie:viewingRooms.erreurs.generique')}</p>
      )}
    </div>
  );
}
